import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { X, MapPin, CreditCard, Tag, Award, Package, Calendar, Mail, User, Phone, FileText, RefreshCw } from "lucide-react";
import { resolveImageUrl } from "@/components/ImageUploader";
import { toast } from "sonner";

const PAYMENT_LABEL_EL = {
  card: "Κάρτα (Viva)",
  stripe: "Κάρτα (Viva)",  // legacy
  paypal: "PayPal",
  cod: "Αντικαταβολή",
  bank_transfer: "Τραπεζική Κατάθεση",
};

const STATUS_CHIP = {
  paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
  confirmed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  pending_cod: "bg-amber-50 text-amber-800 border-amber-200",
  awaiting_transfer: "bg-amber-50 text-amber-800 border-amber-200",
  awaiting_paypal: "bg-amber-50 text-amber-800 border-amber-200",
  awaiting_payment: "bg-amber-50 text-amber-800 border-amber-200",
  initiated: "bg-blue-50 text-blue-800 border-blue-200",
  cancelled: "bg-red-50 text-red-800 border-red-200",
};

const Row = ({ icon, label, children }) => (
  <div className="flex gap-3 py-2.5 border-b border-[#EBE5DF] last:border-b-0">
    <div className="text-[#D4AF37] mt-0.5">{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] tracking-widest uppercase text-[#736B66] mb-0.5">{label}</p>
      <div className="text-sm text-[#2C1E16] break-words">{children}</div>
    </div>
  </div>
);

const OrderDetailModal = ({ orderId, onClose }) => {
  const { lang } = useLang();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resuming, setResuming] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    setOrder(null);
    setLoading(true);
    api.get(`/orders/${orderId}`)
      .then((r) => setOrder(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orderId]);

  const resumePayment = async () => {
    setResuming(true);
    try {
      const res = await api.post(`/orders/${orderId}/resume-payment`, {
        origin_url: window.location.origin,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Δεν ήταν δυνατή η συνέχιση της πληρωμής");
    } finally {
      setResuming(false);
    }
  };

  const canResumePayment =
    order &&
    (order.payment_method === "card" || order.payment_method === "stripe") &&
    !["paid"].includes(order.payment_status);

  if (!orderId) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="order-detail-modal"
    >
      <div
        className="bg-white max-w-3xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-[#EBE5DF] px-8 py-5 flex justify-between items-start z-10">
          <div>
            <p className="overline mb-1">Παραγγελία</p>
            <h2 className="font-mono text-base text-[#2C1E16]">{orderId}</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:text-[#8B0000]" data-testid="close-order-modal">
            <X size={22} />
          </button>
        </div>

        {loading ? (
          <div className="p-16 text-center text-[#736B66]">Φόρτωση...</div>
        ) : !order ? (
          <div className="p-16 text-center text-[#8B0000]">Αδυναμία φόρτωσης παραγγελίας.</div>
        ) : (
          <div className="px-8 py-6">
            {/* Status chips */}
            <div className="flex flex-wrap gap-2 mb-6">
              <span className={`px-3 py-1 text-[10px] uppercase tracking-widest border ${STATUS_CHIP[order.order_status] || "bg-gray-50 border-gray-200"}`}>
                {order.order_status}
              </span>
              <span className={`px-3 py-1 text-[10px] uppercase tracking-widest border ${STATUS_CHIP[order.payment_status] || "bg-gray-50 border-gray-200"}`}>
                {order.payment_status}
              </span>
            </div>

            {/* Resume payment CTA (unpaid Stripe orders) */}
            {canResumePayment && (
              <div
                className="mb-6 p-4 border border-[#D4AF37]/40 bg-[#FAF7EE] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
                data-testid="resume-payment-banner"
              >
                <div>
                  <p className="text-sm font-semibold text-[#2C1E16] mb-0.5">
                    Η πληρωμή δεν ολοκληρώθηκε
                  </p>
                  <p className="text-xs text-[#736B66]">
                    Συνεχίστε από εδώ για να ολοκληρώσετε την παραγγελία σας με ασφαλή τρόπο.
                  </p>
                </div>
                <button
                  onClick={resumePayment}
                  disabled={resuming}
                  className="luxury-btn whitespace-nowrap"
                  data-testid="resume-payment-button"
                >
                  <RefreshCw size={14} /> {resuming ? "..." : "Συνέχιση πληρωμής"}
                </button>
              </div>
            )}

            {/* Items */}
            <div className="mb-8">
              <h3 className="font-serif text-xl mb-4 flex items-center gap-2">
                <Package size={18} className="text-[#D4AF37]" /> Προϊόντα ({order.items.length})
              </h3>
              <div className="border border-[#EBE5DF]">
                {order.items.map((it, idx) => (
                  <div key={idx} className="flex gap-4 p-4 border-b border-[#EBE5DF] last:border-b-0" data-testid={`order-item-${idx}`}>
                    <div className="w-16 h-16 flex-shrink-0 bg-[#F5F2ED]">
                      {it.image && <img src={resolveImageUrl(it.image)} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-tight">
                        {lang === "el" ? it.name_el : it.name_en}
                      </p>
                      <p className="text-xs text-[#736B66] mt-1">
                        ×{it.quantity} · €{it.unit_price.toFixed(2)}
                      </p>
                    </div>
                    <p className="text-sm font-semibold whitespace-nowrap">
                      €{it.line_total.toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Customer & shipping */}
              <div>
                <h3 className="font-serif text-xl mb-4 flex items-center gap-2">
                  <MapPin size={18} className="text-[#D4AF37]" /> Παραλήπτης
                </h3>
                <div className="space-y-0">
                  <Row icon={<User size={14} />} label="Όνομα">{order.shipping_address.full_name}</Row>
                  <Row icon={<Phone size={14} />} label="Τηλέφωνο">{order.shipping_address.phone}</Row>
                  {order.customer_email && (
                    <Row icon={<Mail size={14} />} label="Email">{order.customer_email}</Row>
                  )}
                  <Row icon={<MapPin size={14} />} label="Διεύθυνση">
                    {order.shipping_address.street}<br />
                    {order.shipping_address.postal_code} {order.shipping_address.city}<br />
                    {order.shipping_address.country}
                  </Row>
                  {order.notes && (
                    <Row icon={<FileText size={14} />} label="Σχόλια">{order.notes}</Row>
                  )}
                </div>
              </div>

              {/* Order details */}
              <div>
                <h3 className="font-serif text-xl mb-4 flex items-center gap-2">
                  <CreditCard size={18} className="text-[#D4AF37]" /> Λεπτομέρειες
                </h3>
                <div className="space-y-0">
                  <Row icon={<Calendar size={14} />} label="Ημερομηνία">
                    {new Date(order.created_at).toLocaleString(lang === "el" ? "el-GR" : "en-US")}
                  </Row>
                  <Row icon={<CreditCard size={14} />} label="Τρόπος πληρωμής">
                    {PAYMENT_LABEL_EL[order.payment_method] || order.payment_method}
                  </Row>
                  {order.coupon && (
                    <Row icon={<Tag size={14} />} label="Κουπόνι">
                      <span className="font-mono font-semibold text-[#D4AF37]">{order.coupon.code}</span>
                      <span className="text-[#736B66] ml-2 text-xs">
                        ({order.coupon.type === "percentage" && `${order.coupon.value}%`}
                        {order.coupon.type === "fixed" && `€${order.coupon.value}`}
                        {order.coupon.type === "free_shipping" && "δωρεάν αποστολή"})
                      </span>
                    </Row>
                  )}
                  {order.loyalty_points_redeemed > 0 && (
                    <Row icon={<Award size={14} />} label="Εξαργύρωση πόντων">
                      <span className="font-mono">{order.loyalty_points_redeemed} πόντοι → €{order.loyalty_discount.toFixed(2)}</span>
                    </Row>
                  )}
                  {order.loyalty_points_earned > 0 && (
                    <Row icon={<Award size={14} />} label="Κερδισμένοι πόντοι">
                      <span className="font-mono">+{order.loyalty_points_earned}</span>
                      {!order.loyalty_earned_credited && (
                        <span className="text-xs text-[#736B66] ml-2">(εκκρεμή — μετά την πληρωμή)</span>
                      )}
                    </Row>
                  )}
                </div>
              </div>
            </div>

            {/* Totals */}
            <div className="mt-8 pt-6 border-t border-[#EBE5DF] bg-[#FAF7EE] -mx-8 px-8 -mb-6 pb-6">
              <table className="w-full text-sm">
                <tbody>
                  <tr>
                    <td className="py-1 text-[#736B66]">Υποσύνολο</td>
                    <td className="py-1 text-right">€{order.subtotal.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td className="py-1 text-[#736B66]">Μεταφορικά</td>
                    <td className="py-1 text-right">{order.shipping > 0 ? `€${order.shipping.toFixed(2)}` : "Δωρεάν"}</td>
                  </tr>
                  {order.discount > 0 && (
                    <tr className="text-[#8B0000]">
                      <td className="py-1">Έκπτωση κουπονιού</td>
                      <td className="py-1 text-right">-€{order.discount.toFixed(2)}</td>
                    </tr>
                  )}
                  {order.loyalty_discount > 0 && (
                    <tr className="text-[#D4AF37]">
                      <td className="py-1">Εξαργύρωση πόντων</td>
                      <td className="py-1 text-right">-€{order.loyalty_discount.toFixed(2)}</td>
                    </tr>
                  )}
                  <tr className="border-t border-[#EBE5DF] font-serif">
                    <td className="pt-3 text-lg">Σύνολο</td>
                    <td className="pt-3 text-right text-xl text-[#D4AF37] font-semibold">€{order.total.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetailModal;
